import axios from 'axios';
import OSS from 'ali-oss';
import { getOssToken } from '@/api/common';
import routerMap from '@/router/map';
import { getSystemDict } from '@/api/common';
import { dayjs } from 'element-plus';

/**
 * 登录态商家ID解析（B 端后台多租户隔离口径）
 *
 * 登录返回结构历史上有三种落点，按优先级依次兜底；解析不出时返回 0（调用方需自行判空）。
 * 统一入口避免各页面各写一份三元表达式（FormRenderer 人员搜索、审批中心等共用）。
 */
export function resolveBusinessId(userInfo: any): number {
    const info = userInfo || {};
    const value =
        info?.extraParams?.businessId || info?.params?.business?.id || info?.params?.businessId || info?.businessId || 0;
    return Number(value) || 0;
}

export async function createAliOssClient() {
    const { data: stsTokeInfo } = await getOssToken();
    return new OSS({
        region: import.meta.env.VITE_OSS_REGION || 'oss-cn-zhangjiakou',
        accessKeyId: stsTokeInfo.access_key_id,
        accessKeySecret: stsTokeInfo.access_key_secret,
        stsToken: stsTokeInfo.security_token,
        refreshSTSToken: async () => {
            const { data } = await getOssToken();
            return {
                accessKeyId: data.access_key_id,
                accessKeySecret: data.access_key_secret,
                stsToken: data.security_token
            };
        },
        refreshSTSTokenInterval: 15 * 60 * 1000,
        bucket: import.meta.env.VITE_OSS_BUCKET_NAME
    });
}

/**
 * 根据获取到的菜单配置数据生成路由配置
 * @param menus
 * @param children
 * @returns
 */
export const buildAdminRoutes = function (menus: Menu.IMenu[], children?: Record<string, unknown>[]) {
    const routes = children || [];

    // 使用 for...of 替代 shift()，避免每次迭代 O(n) 的元素移动开销
    for (const temp of menus) {
        // 管理系统动态数据先手动加上所属系统字段
        const curr: Record<string, unknown> = {
            path: temp.path ? `${temp.path}` : '',
            redirect: temp.redirect,
            meta: {
                label: temp.meta.title,
                show: !temp.meta.hiddenFlag,
                icon: temp.meta.icon,
                functions: temp.meta.functionVOList
            }
        };

        // 只有实际注册了组件的菜单才设置 name，避免目录路由（如 Layout）name 重复导致路由被覆盖
        if (temp.componentName && (routerMap as Record<string, unknown>)[temp.componentName]) {
            curr.name = temp.componentName;
        }

        if (temp.path && (routerMap as Record<string, unknown>)[temp.componentName]) {
            curr.component = (routerMap as Record<string, unknown>)[temp.componentName];
        }

        if (temp.children && temp.children.length) {
            curr.children = [];
            buildAdminRoutes(temp.children, curr.children);
        }
        routes.push(curr);
    }
    return routes;
};

export const getBuildVersion = async () => {
    const {
        data: { version }
    } = await axios.get(`${window.location.origin}/version.json`, {
        headers: { 'Cache-Control': 'no-cache' }
    });

    return version;
};

/**
 * 获取系统字典公共方法
 */
export const getSysDictList = async (parentCodeExtras: string[]) => {
    try {
        const { result } = await getSystemDict({ parentCodeExtras });
        const dicts: Record<string, Common.IDictItem[]> = {};
        for (const key in result) {
            if (Object.prototype.hasOwnProperty.call(result, key)) {
                dicts[key] = result[key].map((item: Record<string, unknown>) => ({
                    label: item.name,
                    value: item.code
                }));
            }
        }

        return dicts;
    } catch (error) {
        return {};
    }
};

/**
 *
 */
export const generateShortcuts = (noFuture: boolean = false) => {
    const formatter = 'YYYY-MM-DD HH:mm:ss';
    return {
        yesterday: {
            text: '昨天',
            value: () => {
                const start = dayjs().subtract(1, 'day').startOf('day');
                const end = dayjs().subtract(1, 'day').endOf('day');
                return [start.format(formatter), end.format(formatter)];
            }
        },
        today: {
            text: '今天',
            value: () => {
                const start = dayjs().startOf('day');
                const end = dayjs().endOf('day');
                return [start.format(formatter), end.format(formatter)];
            }
        },
        tomorrow: {
            text: '明天',
            value: () => {
                const start = dayjs().add(1, 'day').startOf('day');
                const end = dayjs().add(1, 'day').endOf('day');
                return [start.format(formatter), end.format(formatter)];
            }
        },
        currentWeek: {
            text: '本周',
            value: () => {
                let start = dayjs().startOf('week');
                let end = dayjs().endOf('week');
                // 如果周天是一周的开始，则加一天，周一是每周的第一天
                if (new Date(dayjs(start).format(formatter)).getDay() === 0) {
                    start = start.add(1, 'day');
                    end = end.add(1, 'day');
                }

                return [start.format(formatter), end.format(formatter)];
            }
        },
        lastWeek: {
            text: '上周',
            value: () => {
                let start = dayjs().subtract(1, 'week').startOf('week');
                let end = dayjs().subtract(1, 'week').endOf('week');
                // 如果周天是一周的开始，则加一天，周一是每周的第一天
                if (new Date(dayjs(start).format(formatter)).getDay() === 0) {
                    start = start.add(1, 'day');
                    end = end.add(1, 'day');
                }

                return [start.format(formatter), end.format(formatter)];
            }
        },
        currentMonth: {
            text: '本月',
            value: () => {
                const start = dayjs().startOf('month');
                const end = dayjs().endOf('month');
                return [start.format(formatter), end.format(formatter)];
            }
        },
        lastMonth: {
            text: '上月',
            value: () => {
                const start = dayjs().subtract(1, 'month').startOf('month');
                const end = dayjs().subtract(1, 'month').endOf('month');
                return [start.format(formatter), end.format(formatter)];
            }
        }
    };
};
